import { ConfirmationDialog } from '@/components/patterns/confirmation-dialog'
import { ORDER_ASIDE_FORM_SURFACE_CLASS } from '@/components/orders/order-aside.styles'
import { OrderAside } from '@/components/orders/OrderAside'
import { OrderAsidePanelHeader, OrderDetailPanel } from '@/components/orders/OrderDetailPanel'
import { SelectableSurface } from '@/components/patterns/selectable-surface'
import { ActionSurface } from '@/components/patterns/action-surface'
import ReportDateRangeFilterBar from '@/components/reports/ReportDateRangeFilterBar'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { popoverContentVariants } from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import { SegmentedControl, SegmentedControlItem } from '@/components/ui/segmented-control'
import { StyledIcon } from '@/components/ui/styled-icon'
import { ToggleButton } from '@/components/ui/toggle-button'
import type { StyledIconTone } from '@/components/ui/styled-icon'
import { Typography, type TypographyProps } from '@/components/ui/typography'
import dayjs from '@/lib/dayjs'
import { serverFieldErrors, submitSchemaValidator, useAppForm } from '@/lib/form'
import {
  useCreateAvailabilityChangeMutation,
  useSoftDeleteAvailabilityChangeMutation,
  useUpdateAvailabilityChangeMutation,
} from '@/lib/services/availability-changes/availability-changes.query'
import { availabilityChangeSchema } from '@/lib/services/availability-changes/availability-changes.schema'
import type {
  AvailabilityCalendarOccurrence,
  AvailabilityChange,
} from '@/lib/services/availability-changes/availability-changes.types'
import { locationFilters, locationQueries } from '@/lib/services/locations/locations.query'
import { storeQueries } from '@/lib/services/stores/stores.query'
import {
  DESIGNERS_CHOICE_ONLY_EMPTY_COPY,
  hasAvailableDesignersChoiceProducts,
} from '@/lib/services/stores/stores.utils'
import { cn, getErrorMessage, isRecord } from '@/lib/utils'
import type { Location } from '@/lib/services/locations/locations.types'
import { useQuery } from '@tanstack/react-query'
import type { FormAsyncValidateOrFn, FormValidateOrFn } from '@tanstack/react-form'
import {
  Ban,
  CalendarCheck,
  CalendarPlus,
  Check,
  Clock,
  Flower2,
  MapPin,
  Repeat2,
  ShoppingBasket,
  Trash,
  Truck,
  X,
  type LucideIcon,
} from 'lucide-react'
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
  type RefObject,
  type SubmitEvent,
} from 'react'
import { toast } from 'sonner'
import {
  buildTemporaryHoursPayload,
  formatQuickCreateDateRange,
  getActiveChangeSelectedLocationIds,
  getDefaultTemporaryHours,
  getMaxTemporaryOpenTime,
  getMinTemporaryCloseTime,
  getQuickCreateAvailabilityPayload,
  getQuickCreateEffects,
  getQuickCreatePopoverPosition,
  getChangeDateInputValue,
  getChangeEndDate,
  getChangeIdentifier,
  getChangeStartDate,
  getTemporaryHoursFormValue,
  getTemporaryHoursChangeLocationIds,
  changeHasTemporaryHours,
  type AvailabilityEffectFlags,
  type TemporaryHoursFormValue,
} from '@/lib/services/availability-changes/availability-changes.utils'
import { getLocationName } from '@/lib/services/locations/locations.utils'
import { getAvailabilityChangePresentation } from './availability-change.presentation'
import type { AvailabilityCalendarAnchor } from './AvailabilityChangesMonthView'

const quickCreateModes = {
  availability: 'availability',
  temporaryHours: 'temporary_hours',
} as const

export type AvailabilityQuickCreateMode = (typeof quickCreateModes)[keyof typeof quickCreateModes]

/** A saved change to edit, or the calendar occurrence it was opened from. */
type EditableAvailabilityChange = AvailabilityChange | AvailabilityCalendarOccurrence

/** What the quick-create editor opens on: its dates, and the change to edit or effects to start with. */
export interface AvailabilityQuickCreateSelection {
  /** `YYYY-MM-DD` */
  startDate: string
  endDate: string
  /** Where the chooser popover opens. */
  anchor?: AvailabilityCalendarAnchor | null | undefined
  /** Without one, the editor first asks which kind of change to schedule. */
  mode?: AvailabilityQuickCreateMode | null
  presentation?: 'popover' | 'sheet'
  change?: EditableAvailabilityChange | null
  initialEffects?: Partial<AvailabilityEffectFlags>
}

/** The `temporary_hours` lanes the form edits. */
type TemporaryHoursLaneType = keyof TemporaryHoursFormValue

const quickCreateModeOptions: {
  value: AvailabilityQuickCreateMode
  label: string
  description: string
  icon: LucideIcon
  tone: StyledIconTone
}[] = [
  {
    value: quickCreateModes.temporaryHours,
    label: 'Temporary hours',
    description: 'Set different pickup or delivery hours just for the selected dates.',
    icon: Clock,
    tone: getAvailabilityChangePresentation('temporary_hours').tone,
  },
  {
    value: quickCreateModes.availability,
    label: 'Close or limit orders',
    description: "Close the store, pause pickup or delivery, or require Designer's Choice.",
    icon: Ban,
    tone: 'warning',
  },
]

const temporaryHoursTimeSelectClassNames = {
  selectTrigger: 'h-9 bg-background/80',
  selectContent: 'max-h-52',
}

const quickCreatePortalContentSelector = [
  '[data-slot="popover-content"]',
  '[data-slot="select-content"]',
].join(',')

const quickCreateDateRangePickerClassName = 'flex justify-start'
const quickCreateDateRangeTriggerClassName = 'w-full justify-start text-left'
const quickCreateScrollViewportClassName = 'overscroll-contain'
const quickCreateFormSurfaceClassName = cn(ORDER_ASIDE_FORM_SURFACE_CLASS, 'space-y-5')
const quickCreateFooterClassName = 'shrink-0 px-5 pt-4 pb-5 sm:px-6'
const quickCreateFieldGroupClassName = 'space-y-2'
const quickCreateFieldLabelProps: Pick<TypographyProps, 'className' | 'tone' | 'weight'> = {
  className: 'block',
  tone: 'muted',
  weight: 'medium',
}
const locationRequiredMessage = 'Select at least one location before scheduling availability.'
const noActiveLocationsMessage =
  'Enable or create an active location before scheduling availability.'
const quickCreateIconShellBaseClassName =
  'flex shrink-0 items-center justify-center rounded-lg bg-background text-muted-foreground ring-1 ring-inset ring-border/70'
const quickCreateSelectedIconShellClassName =
  'text-interaction ring-primary/25 dark:bg-background/40'
const quickCreateFooterButtonGroupClassName = 'flex items-center gap-2'
const quickCreateDateRangeHelperText =
  'Need more than one day? Choose a start and end date, or drag across days on the calendar.'

const DESIGNERS_CHOICE_ONLY_NO_FULFILLMENT_COPY =
  "Designer's Choice only applies when pickup or delivery is still available."

function isQuickCreatePortalInteraction(target: EventTarget | null): boolean {
  return target instanceof Element && Boolean(target.closest(quickCreatePortalContentSelector))
}

function selectedLocationsCanLimitDesignersChoice({
  effects,
  locations,
  selectedLocationIds,
}: {
  effects: AvailabilityEffectFlags
  locations: Location[]
  selectedLocationIds: string[]
}): boolean {
  if (!selectedLocationIds.length) {
    return false
  }

  const selectedIds = new Set(selectedLocationIds.map(String))

  return locations.some((location) => {
    if (!selectedIds.has(String(location.id))) {
      return false
    }

    return (
      (Boolean(location.is_pickup) && !effects.pickup_paused) ||
      (Boolean(location.is_delivery) && !effects.delivery_paused)
    )
  })
}

/** Laravel validation errors, keyed by field. */
type ServerErrors = Record<string, unknown>

function getServerErrorMessages(errors: ServerErrors, fieldNames: string[]): string[] {
  return fieldNames.flatMap((fieldName) => {
    const messages = errors[fieldName]

    if (Array.isArray(messages)) {
      return messages.map(String)
    }

    return typeof messages === 'string' && messages ? [messages] : []
  })
}

function QuickCreateServerErrors({ errors, fields }: { errors: ServerErrors; fields: string[] }) {
  const messages = getServerErrorMessages(errors, fields)

  if (messages.length === 0) {
    return null
  }

  return (
    <div
      role="alert"
      className="rounded-md border border-status-destructive-border bg-status-destructive-background px-3 py-2 text-status-destructive-foreground"
    >
      {messages.map((message) => (
        <Typography variant="label" key={message} className="block">
          {message}
        </Typography>
      ))}
    </div>
  )
}

function QuickCreateSurface({
  ariaLabel,
  children,
  className,
  isSheet,
  onClose,
  onSheetCloseAnimationEnd,
  onSheetOpenChange,
  onSubmit,
  panelRef,
  rows = 'grid-rows-[auto_minmax(0,1fr)_auto]',
  sheetDescription,
  sheetOpen,
  style,
  type = 'section',
}: {
  ariaLabel: string
  children: ReactNode
  className?: string
  isSheet: boolean
  onClose: () => void
  onSheetCloseAnimationEnd?: (() => void) | undefined
  onSheetOpenChange?: ((open: boolean) => void) | undefined
  onSubmit?: (event: SubmitEvent<HTMLFormElement>) => void
  panelRef: RefObject<HTMLElement | null>
  rows?: string
  sheetDescription?: string
  sheetOpen: boolean
  style?: CSSProperties
  type?: 'section' | 'form'
}) {
  const Element = type
  const content = (
    <Element
      // An object ref can't type-check as both a section's and a form's; a callback ref can.
      ref={(element: HTMLElement | null) => {
        panelRef.current = element
      }}
      role={isSheet ? undefined : 'dialog'}
      aria-label={isSheet ? undefined : ariaLabel}
      className={cn(
        isSheet
          ? 'flex min-h-full flex-col pt-5 pl-0 text-foreground sm:pt-6 sm:pl-1'
          : cn(
              popoverContentVariants({
                size: 'auto',
                padding: 'none',
                elevation: 'strong',
                overflow: 'hidden',
              }),
              'fixed grid overscroll-contain',
              rows
            ),
        className
      )}
      style={isSheet ? undefined : style}
      onSubmit={onSubmit}
    >
      {children}
    </Element>
  )

  if (!isSheet) {
    return content
  }

  return (
    <OrderAside
      open={sheetOpen}
      onOpenChange={
        onSheetOpenChange ??
        ((open: boolean) => {
          if (!open) {
            onClose()
          }
        })
      }
      title={ariaLabel}
      description={sheetDescription ?? ariaLabel}
      onCloseAnimationEnd={onSheetCloseAnimationEnd}
      onOpenAutoFocus={(event) => event.preventDefault()}
    >
      {content}
    </OrderAside>
  )
}

function getInitialQuickCreateMode(
  selection: AvailabilityQuickCreateSelection | null
): AvailabilityQuickCreateMode | null {
  if (!selection) {
    return null
  }

  if (selection.mode) {
    return selection.mode
  }

  if (selection.change) {
    return changeHasTemporaryHours(selection.change)
      ? quickCreateModes.temporaryHours
      : quickCreateModes.availability
  }

  return null
}

function RecurrenceChoice({
  isRecurringYearly,
  onChange,
}: {
  isRecurringYearly: boolean
  onChange: (isRecurringYearly: boolean) => void
}) {
  return (
    <div className="space-y-2">
      <Typography variant="label" {...quickCreateFieldLabelProps}>
        Repeats
      </Typography>
      <SegmentedControl role="radiogroup" aria-label="Repeats" className="grid w-full grid-cols-2">
        <SegmentedControlItem
          type="button"
          role="radio"
          aria-checked={!isRecurringYearly}
          selected={!isRecurringYearly}
          className="h-auto min-h-10 items-center justify-center gap-2 px-3 py-2 text-left text-sm whitespace-normal"
          onClick={() => onChange(false)}
        >
          <span
            className={cn(
              quickCreateIconShellBaseClassName,
              'size-6',
              !isRecurringYearly && quickCreateSelectedIconShellClassName
            )}
          >
            <CalendarCheck className="size-3.5" />
          </span>
          <span className="min-w-0 truncate">One time</span>
        </SegmentedControlItem>
        <SegmentedControlItem
          type="button"
          role="radio"
          aria-checked={isRecurringYearly}
          selected={isRecurringYearly}
          className="h-auto min-h-10 items-center justify-center gap-2 px-3 py-2 text-left text-sm whitespace-normal"
          onClick={() => onChange(true)}
        >
          <span
            className={cn(
              quickCreateIconShellBaseClassName,
              'size-6',
              isRecurringYearly && quickCreateSelectedIconShellClassName
            )}
          >
            <Repeat2 className="size-3.5" />
          </span>
          <span className="min-w-0 truncate">Every year</span>
        </SegmentedControlItem>
      </SegmentedControl>
    </div>
  )
}

/** The temporary hours form's values: a fixed-date change of hours only. */
interface TemporaryHoursChangeFormValues {
  id: number | undefined
  availabilityChangeId: number | undefined
  organization_id: number
  location_ids: string[]
  /** `YYYY-MM-DD` */
  start_date: string
  end_date: string
  store_closed: boolean
  pickup_paused: boolean
  delivery_paused: boolean
  designers_choice_only: boolean
  temporary_hours: TemporaryHoursFormValue
  repeats_annually: boolean
  holiday_key: null
  start_offset_days: null
  end_offset_days: null
}

function useTemporaryHoursChangeForm(options: {
  defaultValues: TemporaryHoursChangeFormValues
  validators: {
    onSubmit: FormValidateOrFn<TemporaryHoursChangeFormValues>
    onSubmitAsync: FormAsyncValidateOrFn<TemporaryHoursChangeFormValues>
  }
}) {
  return useAppForm(options)
}

type TemporaryHoursFormApi = ReturnType<typeof useTemporaryHoursChangeForm>

function TemporaryHoursServiceRow({
  form,
  icon: Icon,
  label,
  type,
}: {
  form: TemporaryHoursFormApi
  icon: LucideIcon
  label: string
  type: TemporaryHoursLaneType
}) {
  const id = `temporary-hours-${type}`

  return (
    <form.AppField name={`temporary_hours.${type}.is_open`}>
      {(isOpenField) => {
        const isOpen = Boolean(isOpenField.state.value)

        return (
          <Card
            size="flush"
            surface={isOpen ? 'brandSubtle' : 'subtle'}
            selected={isOpen}
            className="px-3 py-3"
          >
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <span className="flex min-w-0 items-center gap-3">
                  <StyledIcon
                    icon={Icon}
                    tone={isOpen ? 'success' : 'neutral'}
                    iconClassName="size-4"
                  />
                  <span className="min-w-0 truncate text-sm font-semibold text-foreground">
                    {label}
                  </span>
                </span>

                <isOpenField.SwitchInput
                  id={`${id}-is-open`}
                  label={isOpen ? 'Custom' : 'Regular'}
                  className="w-auto shrink-0"
                  onCheckedChange={(isChecked) => {
                    isOpenField.handleChange(isChecked)

                    if (isChecked) {
                      if (!form.getFieldValue(`temporary_hours.${type}.open`)) {
                        form.setFieldValue(`temporary_hours.${type}.open`, '09:00')
                      }

                      if (!form.getFieldValue(`temporary_hours.${type}.close`)) {
                        form.setFieldValue(`temporary_hours.${type}.close`, '17:00')
                      }
                    }
                  }}
                />
              </div>

              {isOpen ? (
                <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2">
                  <div className="min-w-0">
                    <form.Subscribe
                      selector={(state) => state.values.temporary_hours?.[type]?.close}
                    >
                      {(closeTime) => (
                        <form.AppField name={`temporary_hours.${type}.open`}>
                          {(openField) => (
                            <openField.HourInput
                              id={`${id}-open`}
                              ariaLabel={`${label} start time`}
                              minHour="00:00"
                              maxHour={getMaxTemporaryOpenTime(closeTime)}
                              className="min-w-0"
                              classNames={temporaryHoursTimeSelectClassNames}
                            />
                          )}
                        </form.AppField>
                      )}
                    </form.Subscribe>
                  </div>
                  <Typography variant="label" tone="muted">
                    to
                  </Typography>
                  <div className="min-w-0">
                    <form.Subscribe
                      selector={(state) => state.values.temporary_hours?.[type]?.open}
                    >
                      {(openTime) => (
                        <form.AppField name={`temporary_hours.${type}.close`}>
                          {(closeField) => (
                            <closeField.HourInput
                              id={`${id}-close`}
                              ariaLabel={`${label} end time`}
                              minHour={getMinTemporaryCloseTime(openTime)}
                              maxHour="23:59"
                              className="min-w-0"
                              classNames={temporaryHoursTimeSelectClassNames}
                            />
                          )}
                        </form.AppField>
                      )}
                    </form.Subscribe>
                  </div>
                </div>
              ) : null}
            </div>
          </Card>
        )
      }}
    </form.AppField>
  )
}

function TemporaryHoursChangeForm({
  canDelete = false,
  deleteDialogOpen,
  initialEndDate = null,
  initialLocationIds = null,
  initialChange = null,
  initialStartDate = null,
  onCancel,
  onDeleted,
  onDeleteDialogOpenChange,
  onSaved,
  organizationId,
  sheetIcon = Clock,
  sheetSubtitle = null,
  sheetTitle = 'Hour Change',
}: {
  canDelete?: boolean
  deleteDialogOpen?: boolean
  initialEndDate?: string | null
  initialLocationIds?: string[] | null
  initialChange?: EditableAvailabilityChange | null
  initialStartDate?: string | null
  onCancel: () => void
  onDeleted?: () => void
  onDeleteDialogOpenChange?: (open: boolean) => void
  onSaved?: () => void
  organizationId: number
  sheetIcon?: LucideIcon
  sheetSubtitle?: string | null
  sheetTitle?: string
}) {
  const [internalDeleteDialogOpen, setInternalDeleteDialogOpen] = useState(false)
  const [showLocationRequiredError, setShowLocationRequiredError] = useState(false)
  const createMutation = useCreateAvailabilityChangeMutation()
  const updateMutation = useUpdateAvailabilityChangeMutation()
  const deleteMutation = useSoftDeleteAvailabilityChangeMutation()
  const mutation = initialChange ? updateMutation : createMutation
  const changeId = getChangeIdentifier(initialChange)
  const isDeleteDialogOpen = deleteDialogOpen ?? internalDeleteDialogOpen
  const setIsDeleteDialogOpen = onDeleteDialogOpenChange ?? setInternalDeleteDialogOpen
  const { data: locations = [] } = useQuery({
    ...locationQueries.index({
      organizationId,
      filters: locationFilters().get(),
    }),
    enabled: Boolean(organizationId),
  })
  const activeLocations = useMemo(
    () => locations.filter((candidate) => candidate?.is_active !== false),
    [locations]
  )
  const activeLocationIds = useMemo(
    () => activeLocations.map((candidate) => String(candidate.id)),
    [activeLocations]
  )
  const selectedLocationIds = useMemo(() => {
    const normalizedInitialLocationIds = Array.isArray(initialLocationIds)
      ? initialLocationIds.map(String).filter(Boolean)
      : []

    if (!initialChange && normalizedInitialLocationIds.length > 0) {
      return normalizedInitialLocationIds
    }

    return getTemporaryHoursChangeLocationIds(initialChange, activeLocationIds)
  }, [activeLocationIds, initialLocationIds, initialChange])
  const shouldShowLocationPicker = activeLocations.length > 1
  const startDateValue =
    getChangeDateInputValue(getChangeStartDate(initialChange)) ??
    getChangeDateInputValue(initialStartDate) ??
    dayjs().format('YYYY-MM-DD')
  const endDateValue =
    getChangeDateInputValue(getChangeEndDate(initialChange)) ??
    getChangeDateInputValue(initialEndDate) ??
    startDateValue

  const form = useTemporaryHoursChangeForm({
    defaultValues: {
      id: changeId,
      availabilityChangeId: changeId,
      organization_id: organizationId,
      location_ids: selectedLocationIds,
      start_date: startDateValue,
      end_date: endDateValue,
      store_closed: false,
      pickup_paused: false,
      delivery_paused: false,
      designers_choice_only: false,
      temporary_hours: initialChange
        ? getTemporaryHoursFormValue(initialChange)
        : getDefaultTemporaryHours(),
      repeats_annually: Boolean(initialChange?.repeats_annually),
      holiday_key: null,
      start_offset_days: null,
      end_offset_days: null,
    },
    validators: {
      onSubmit: submitSchemaValidator(availabilityChangeSchema),
      onSubmitAsync: async ({ value }) => {
        try {
          const formLocationIds = Array.isArray(value.location_ids)
            ? value.location_ids.map(String).filter(Boolean)
            : []
          const payloadLocationIds =
            formLocationIds.length > 0 || shouldShowLocationPicker
              ? formLocationIds
              : selectedLocationIds

          if (payloadLocationIds.length === 0) {
            return { fields: { location_ids: ['Choose at least one location.'] } }
          }

          const payload = buildTemporaryHoursPayload({
            existingChange: initialChange,
            organizationId,
            selectedLocationIds: payloadLocationIds,
            value,
          })

          if (initialChange) {
            const availabilityChangeId = getChangeIdentifier(initialChange)

            await updateMutation.mutateAsync({
              ...payload,
              id: availabilityChangeId,
              availabilityChangeId,
            })
          } else {
            await createMutation.mutateAsync(payload)
          }

          toast.success(initialChange ? 'Temporary hours updated' : 'Temporary hours scheduled')
          onSaved?.()
        } catch (error) {
          toast.error(
            initialChange
              ? 'Failed to update temporary hours'
              : 'Failed to schedule temporary hours',
            {
              description: getErrorMessage(error),
            }
          )

          return serverFieldErrors(error)
        }

        return undefined
      },
    },
  })

  const handleDelete = async () => {
    if (!changeId) {
      return
    }

    try {
      await deleteMutation.mutateAsync(changeId)
      toast.success('Availability change deleted')
      onDeleted?.()
    } catch (error) {
      toast.error('Failed to delete availability change', {
        description: getErrorMessage(error),
      })
    }
  }

  useEffect(() => {
    if (selectedLocationIds.length === 0) {
      return
    }

    const currentLocationIds = form.getFieldValue('location_ids') ?? []
    if (currentLocationIds.length === 0) {
      form.setFieldValue('location_ids', selectedLocationIds)
    }
  }, [form, selectedLocationIds])

  const fields = (
    <>
      <section className="space-y-2">
        <form.Subscribe
          selector={(state) => [state.values.start_date, state.values.end_date] as const}
        >
          {([startDate, endDate]) => (
            <>
              <Typography variant="label" {...quickCreateFieldLabelProps}>
                Dates
              </Typography>
              <ReportDateRangeFilterBar
                calendarIntent="future"
                className={quickCreateDateRangePickerClassName}
                triggerClassName={quickCreateDateRangeTriggerClassName}
                initialFilters={{
                  start_date: startDate,
                  end_date: endDate,
                  minDate: dayjs().format('YYYY-MM-DD'),
                }}
                presetKeys={[]}
                showSingleDayAsRange
                showTriggerChevron
                onApply={({ start_date, end_date }) => {
                  form.setFieldValue('start_date', start_date)
                  form.setFieldValue('end_date', end_date)
                }}
              />
              <Typography variant="caption" className="block">
                {quickCreateDateRangeHelperText}
              </Typography>
            </>
          )}
        </form.Subscribe>
      </section>

      <section className="space-y-2">
        <Typography variant="label" {...quickCreateFieldLabelProps}>
          Hours
        </Typography>
        <div className="grid gap-2">
          <TemporaryHoursServiceRow
            form={form}
            type="pickup"
            label="Pickup hours"
            icon={ShoppingBasket}
          />
          <TemporaryHoursServiceRow
            form={form}
            type="delivery"
            label="Delivery hours"
            icon={Truck}
          />
        </div>
      </section>

      {shouldShowLocationPicker ? (
        <section className="space-y-2">
          <Typography variant="label" {...quickCreateFieldLabelProps}>
            Locations
          </Typography>
          <form.AppField name="location_ids">
            {(field) => {
              const fieldLocationIds = Array.isArray(field.state.value)
                ? field.state.value.map(String)
                : []

              const toggleLocation = (locationId: string) => {
                const nextLocationIds = fieldLocationIds.includes(locationId)
                  ? fieldLocationIds.filter((currentLocationId) => currentLocationId !== locationId)
                  : [...fieldLocationIds, locationId]

                setShowLocationRequiredError(false)
                field.handleChange(nextLocationIds)
              }

              return (
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-2">
                    {activeLocations.map((location) => {
                      const locationId = String(location.id)
                      const isSelected = fieldLocationIds.includes(locationId)

                      return (
                        <ToggleButton
                          key={locationId}
                          type="button"
                          pressed={isSelected}
                          size="xs"
                          className="min-w-0 gap-2 px-2.5 has-[>svg]:px-2.5"
                          onClick={() => toggleLocation(locationId)}
                        >
                          {isSelected ? (
                            <Check className="size-3.5 shrink-0" />
                          ) : (
                            <MapPin className="size-3.5 shrink-0" />
                          )}
                          <span className="max-w-44 truncate">{getLocationName(location)}</span>
                        </ToggleButton>
                      )
                    })}
                  </div>
                  {showLocationRequiredError && fieldLocationIds.length === 0 ? (
                    <Typography variant="label" tone="destructive" className="block">
                      {locationRequiredMessage}
                    </Typography>
                  ) : null}
                </div>
              )
            }}
          </form.AppField>
        </section>
      ) : activeLocations.length === 0 ? (
        <Alert variant="destructive">
          <AlertDescription>{noActiveLocationsMessage}</AlertDescription>
        </Alert>
      ) : null}

      <form.AppField name="repeats_annually">
        {(field) => (
          <RecurrenceChoice
            isRecurringYearly={Boolean(field.state.value)}
            onChange={field.handleChange}
          />
        )}
      </form.AppField>
    </>
  )
  const footer = (
    <div
      data-slot="availability-sheet-footer"
      className={cn(
        'flex shrink-0 flex-wrap items-center justify-between gap-2',
        quickCreateFooterClassName
      )}
    >
      {initialChange && canDelete ? (
        <ConfirmationDialog
          variant="destructive"
          title="Delete availability change"
          description="This removes the temporary hour adjustment from the selected dates."
          confirmText="Delete"
          pendingText="Deleting..."
          open={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
          isPending={deleteMutation.isPending}
          onConfirm={handleDelete}
          trigger={
            <Button type="button" variant="ghostDestructive" className="mr-auto">
              <Trash className="size-4" />
              Delete
            </Button>
          }
        />
      ) : null}
      <div className={cn('ml-auto', quickCreateFooterButtonGroupClassName)}>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <form.Subscribe
          selector={(state) =>
            [state.values.location_ids, state.canSubmit, state.isSubmitting, state.errors] as const
          }
        >
          {([formLocationIds, canSubmit, isSubmitting, errors]) => {
            const hasSelectedLocation =
              Array.isArray(formLocationIds) && formLocationIds.filter(Boolean).length > 0
            const isLoading = isSubmitting || mutation?.isPending
            const isDisabled =
              !hasSelectedLocation || Boolean(errors.length) || isLoading || !canSubmit

            return (
              <div
                className="inline-flex"
                onClick={() => {
                  if (!hasSelectedLocation) {
                    setShowLocationRequiredError(true)
                  }
                }}
              >
                <Button
                  type="submit"
                  disabled={isDisabled}
                  className="w-auto min-w-32 whitespace-nowrap"
                >
                  {isLoading ? 'Saving...' : initialChange ? 'Save hours' : 'Schedule hours'}
                </Button>
              </div>
            )
          }}
        </form.Subscribe>
      </div>
    </div>
  )

  return (
    <form.AppForm>
      <form.Form className="flex flex-col">
        <OrderDetailPanel
          title={sheetTitle}
          subtitle={sheetSubtitle}
          icon={sheetIcon}
          tone="periwinkle"
          className="shadow-none"
        >
          <div className={quickCreateFormSurfaceClassName}>{fields}</div>
        </OrderDetailPanel>
        {footer}
      </form.Form>
    </form.AppForm>
  )
}

/** An effect the closure editor toggles. */
type QuickCreateEffectOption = {
  value: keyof AvailabilityEffectFlags
  label: string
  description: string
  icon: LucideIcon
}

const quickCreateStoreClosedOption: QuickCreateEffectOption = {
  value: 'store_closed',
  label: 'Store Closed',
  description: 'Shows the location(s) as closed on the website for these dates.',
  icon: Ban,
}

const quickCreateWebsiteEffectOptions: QuickCreateEffectOption[] = [
  {
    value: 'pickup_paused',
    label: 'Pickup Pause',
    description: 'Prevents website shoppers from selecting pickup dates in this range.',
    icon: ShoppingBasket,
  },
  {
    value: 'delivery_paused',
    label: 'Delivery Pause',
    description: 'Prevents website shoppers from selecting delivery dates in this range.',
    icon: Truck,
  },
  {
    value: 'designers_choice_only',
    label: 'Designer’s Choice Only',
    description: 'Requires website orders in this range to contain only Designer’s Choice items.',
    icon: Flower2,
  },
]

// Shared by the availability calendar and contextual storefront availability actions.
export function AvailabilityQuickCreatePopover({
  canDelete = false,
  onClose,
  onClosed,
  onModeSelect,
  onOpenChange,
  open = false,
  organizationId,
  selection,
}: {
  canDelete?: boolean
  onClose: () => void
  onClosed?: () => void
  /** The popover hands a chosen mode to its owner, which reopens the editor as a sheet. */
  onModeSelect?: (mode: AvailabilityQuickCreateMode) => void
  onOpenChange?: (open: boolean) => void
  open?: boolean
  organizationId: number
  selection: AvailabilityQuickCreateSelection | null
}): ReactElement | null {
  const panelRef = useRef<HTMLElement>(null)
  const editingChange = selection?.change ?? null
  const isEditing = Boolean(editingChange)
  const [mode, setMode] = useState(() => getInitialQuickCreateMode(selection))
  const isSheet = selection?.presentation === 'sheet' || Boolean(mode)
  const [effects, setEffects] = useState(() =>
    getQuickCreateEffects(editingChange ?? selection?.initialEffects)
  )
  const [isRecurringYearly, setIsRecurringYearly] = useState(() =>
    Boolean(editingChange?.repeats_annually)
  )
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [manualLocationIds, setManualLocationIds] = useState<string[] | null>(null)
  const [serverErrorState, setServerErrorState] = useState<{
    key: string | null
    errors: ServerErrors
  }>({ key: null, errors: {} })
  const [submitHelpMessage, setSubmitHelpMessage] = useState('')
  const [startDate, setStartDate] = useState(selection?.startDate ?? dayjs().format('YYYY-MM-DD'))
  const [endDate, setEndDate] = useState(selection?.endDate ?? selection?.startDate ?? startDate)
  const createMutation = useCreateAvailabilityChangeMutation()
  const updateMutation = useUpdateAvailabilityChangeMutation()
  const deleteMutation = useSoftDeleteAvailabilityChangeMutation()
  const mutation = isEditing ? updateMutation : createMutation
  const changeId = getChangeIdentifier(editingChange)
  const { data: store } = useQuery({
    ...storeQueries.show({ organizationId }),
    enabled: Boolean(organizationId && selection),
  })
  const canUseDesignersChoiceOnly = hasAvailableDesignersChoiceProducts(store)
  const { data: locations = [] } = useQuery({
    ...locationQueries.index({
      organizationId,
      filters: locationFilters().get(),
    }),
    enabled: Boolean(organizationId && selection),
  })
  const activeLocations = useMemo(
    () => locations.filter((candidate) => candidate?.is_active !== false),
    [locations]
  )
  const activeLocationIds = useMemo(
    () => activeLocations.map((candidate) => String(candidate.id)),
    [activeLocations]
  )
  const shouldShowLocationPicker = activeLocations.length > 1
  const initialChangeLocationIds = editingChange
    ? getActiveChangeSelectedLocationIds(editingChange, activeLocationIds)
    : []
  const defaultSelectedLocationIds = isEditing ? initialChangeLocationIds : activeLocationIds
  const selectedLocationIds = shouldShowLocationPicker
    ? (manualLocationIds ?? defaultSelectedLocationIds)
    : defaultSelectedLocationIds
  const selectedLocationCount = selectedLocationIds.length
  const endDateIsValid =
    dayjs(endDate).isValid() && !dayjs(endDate).isBefore(dayjs(startDate), 'day')
  const startDateIsValid = dayjs(startDate).isValid()
  const hasEffect = Boolean(
    effects.store_closed ||
    effects.pickup_paused ||
    effects.delivery_paused ||
    effects.designers_choice_only
  )
  const selectedLocationsCanUseDesignersChoice = selectedLocationsCanLimitDesignersChoice({
    effects,
    locations: activeLocations,
    selectedLocationIds,
  })
  const nextDesignersChoiceEffects = {
    ...effects,
    pickup_paused: effects.pickup_paused && effects.delivery_paused ? false : effects.pickup_paused,
    delivery_paused:
      effects.pickup_paused && effects.delivery_paused ? false : effects.delivery_paused,
    designers_choice_only: true,
  }
  const selectedLocationsCanToggleDesignersChoice = selectedLocationsCanLimitDesignersChoice({
    effects: nextDesignersChoiceEffects,
    locations: activeLocations,
    selectedLocationIds,
  })
  const canSave = Boolean(
    selectedLocationCount > 0 &&
    hasEffect &&
    startDateIsValid &&
    endDateIsValid &&
    selection &&
    (!effects.designers_choice_only || canUseDesignersChoiceOnly) &&
    (!effects.designers_choice_only || selectedLocationsCanUseDesignersChoice) &&
    !(effects.designers_choice_only && effects.pickup_paused && effects.delivery_paused)
  )
  const serverErrorScopeKey = [
    mode ?? 'choose',
    changeId ?? 'new',
    startDate ?? '',
    endDate ?? '',
    selectedLocationIds.join(','),
    effects.store_closed ? 1 : 0,
    effects.pickup_paused ? 1 : 0,
    effects.delivery_paused ? 1 : 0,
    effects.designers_choice_only ? 1 : 0,
    isRecurringYearly ? 1 : 0,
  ].join('|')
  const serverErrors = serverErrorState.key === serverErrorScopeKey ? serverErrorState.errors : {}
  const clearServerErrors = () => setServerErrorState({ key: null, errors: {} })
  const clearSubmitHelp = () => setSubmitHelpMessage('')

  const closeSurface = () => {
    if (!isSheet) {
      onClose()
      return
    }

    onOpenChange?.(false)
  }

  useEffect(() => {
    const isSurfaceOpen = Boolean(selection) && (!isSheet || open)

    if (!isSurfaceOpen) {
      return
    }

    document.body.setAttribute('data-availability-quick-create-open', 'true')

    return () => {
      document.body.removeAttribute('data-availability-quick-create-open')
    }
  }, [isSheet, open, selection])

  useEffect(() => {
    if (!selection || isSheet) {
      return
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (
        isDeleteDialogOpen ||
        (event.target instanceof Node && panelRef.current?.contains(event.target)) ||
        isQuickCreatePortalInteraction(event.target)
      ) {
        return
      }

      onClose()
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isDeleteDialogOpen, isSheet, onClose, selection])

  if (!selection) {
    return null
  }

  const handleModeSelect = (nextMode: AvailabilityQuickCreateMode) => {
    if (!isSheet && onModeSelect) {
      onModeSelect(nextMode)
      return
    }

    setMode(nextMode)
    clearServerErrors()
  }

  if (!mode) {
    const chooserFields = (
      <div>
        <div className="grid gap-2">
          {quickCreateModeOptions.map((option) => {
            const Icon = option.icon

            return (
              <ActionSurface
                key={option.value}
                padding="compact"
                className="w-full items-start gap-3"
                onClick={() => handleModeSelect(option.value)}
              >
                <StyledIcon
                  icon={Icon}
                  tone={option.tone}
                  className="mt-0.5"
                  iconClassName="size-4"
                />
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-foreground">
                    {option.label}
                  </span>
                  <span className="mt-0.5 block text-xs leading-5">{option.description}</span>
                </span>
              </ActionSurface>
            )
          })}
        </div>
      </div>
    )

    return (
      <QuickCreateSurface
        ariaLabel="Choose availability change type"
        isSheet={isSheet}
        onClose={closeSurface}
        onSheetCloseAnimationEnd={onClosed}
        onSheetOpenChange={onOpenChange}
        panelRef={panelRef}
        rows="grid-rows-[auto_minmax(0,1fr)]"
        sheetOpen={open}
        style={getQuickCreatePopoverPosition(selection.anchor)}
      >
        {isSheet ? (
          <OrderDetailPanel
            title="Schedule"
            subtitle={formatQuickCreateDateRange({ startDate, endDate })}
            icon={CalendarPlus}
            tone="periwinkle"
            className="shadow-none"
          >
            {chooserFields}
          </OrderDetailPanel>
        ) : (
          <>
            <div className="px-4 pt-4 pb-3">
              <OrderAsidePanelHeader
                title="Schedule"
                subtitle={formatQuickCreateDateRange({ startDate, endDate })}
                icon={CalendarPlus}
                tone="periwinkle"
                action={
                  <Button type="button" variant="ghost" size="icon" onClick={closeSurface}>
                    <X className="size-4" />
                    <span className="sr-only">Close</span>
                  </Button>
                }
              />
            </div>

            <ScrollArea
              className="min-h-0 flex-1 px-4 pb-4"
              viewportClassName={quickCreateScrollViewportClassName}
            >
              {chooserFields}
            </ScrollArea>
          </>
        )}
      </QuickCreateSurface>
    )
  }

  if (mode === quickCreateModes.temporaryHours) {
    const sheetTitle = 'Hour Change'
    const sheetSubtitle = isEditing
      ? 'Update temporary pickup or delivery hours for these dates.'
      : 'Set temporary pickup or delivery hours for these dates.'

    return (
      <QuickCreateSurface
        ariaLabel={isEditing ? 'Edit temporary hours' : 'Adjust hours'}
        isSheet={isSheet}
        onClose={closeSurface}
        onSheetCloseAnimationEnd={onClosed}
        onSheetOpenChange={onOpenChange}
        panelRef={panelRef}
        sheetOpen={open}
      >
        <TemporaryHoursChangeForm
          canDelete={canDelete}
          deleteDialogOpen={isDeleteDialogOpen}
          initialEndDate={endDate}
          initialLocationIds={selectedLocationIds}
          initialChange={editingChange}
          initialStartDate={startDate}
          organizationId={organizationId}
          onCancel={closeSurface}
          onDeleted={closeSurface}
          onDeleteDialogOpenChange={setIsDeleteDialogOpen}
          onSaved={closeSurface}
          sheetIcon={Clock}
          sheetSubtitle={sheetSubtitle}
          sheetTitle={sheetTitle}
        />
      </QuickCreateSurface>
    )
  }

  const updateEffects = (
    getNextEffects: (currentEffects: AvailabilityEffectFlags) => AvailabilityEffectFlags
  ) => {
    clearServerErrors()
    clearSubmitHelp()
    setEffects((currentEffects) => getNextEffects(currentEffects))
  }

  const handleEffectToggle = (fieldName: keyof AvailabilityEffectFlags) => {
    updateEffects((currentEffects) => {
      if (fieldName === 'store_closed') {
        if (currentEffects.store_closed) {
          return {
            ...currentEffects,
            store_closed: false,
          }
        }

        return {
          ...currentEffects,
          store_closed: true,
          pickup_paused: true,
          delivery_paused: true,
          designers_choice_only: false,
        }
      }

      if (fieldName === 'designers_choice_only') {
        if (currentEffects.designers_choice_only) {
          return { ...currentEffects, designers_choice_only: false }
        }

        if (!canUseDesignersChoiceOnly) {
          return currentEffects
        }

        const nextEffects = {
          ...currentEffects,
          pickup_paused:
            currentEffects.pickup_paused && currentEffects.delivery_paused
              ? false
              : currentEffects.pickup_paused,
          delivery_paused:
            currentEffects.pickup_paused && currentEffects.delivery_paused
              ? false
              : currentEffects.delivery_paused,
          designers_choice_only: true,
        }

        if (
          !selectedLocationsCanLimitDesignersChoice({
            effects: nextEffects,
            locations: activeLocations,
            selectedLocationIds,
          })
        ) {
          return currentEffects
        }

        return nextEffects
      }

      const nextEffects = {
        ...currentEffects,
        [fieldName]: !currentEffects[fieldName],
      }

      if (nextEffects.pickup_paused && nextEffects.delivery_paused) {
        nextEffects.designers_choice_only = false
      }

      if (
        nextEffects.designers_choice_only &&
        !selectedLocationsCanLimitDesignersChoice({
          effects: nextEffects,
          locations: activeLocations,
          selectedLocationIds,
        })
      ) {
        nextEffects.designers_choice_only = false
      }

      if (nextEffects.store_closed && !nextEffects.pickup_paused && !nextEffects.delivery_paused) {
        nextEffects.store_closed = false
      }

      return nextEffects
    })
  }

  const toggleLocation = (locationId: string) => {
    clearServerErrors()
    clearSubmitHelp()
    setManualLocationIds((currentLocationIds) => {
      const baseLocationIds = currentLocationIds ?? selectedLocationIds

      return baseLocationIds.includes(locationId)
        ? baseLocationIds.filter((currentLocationId) => currentLocationId !== locationId)
        : [...baseLocationIds, locationId]
    })
  }

  const handleSubmit = async (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!canSave) {
      setSubmitHelpMessage(
        selectedLocationCount === 0
          ? locationRequiredMessage
          : 'Choose at least one availability option before saving.'
      )
      return
    }

    try {
      clearServerErrors()
      const payload = {
        ...getQuickCreateAvailabilityPayload({
          effects,
          organizationId,
          selectedLocationIds,
        }),
        repeats_annually: isRecurringYearly,
        start_date: startDate,
        end_date: endDate,
      }

      if (editingChange) {
        const availabilityChangeId = getChangeIdentifier(editingChange)

        await updateMutation.mutateAsync({
          ...payload,
          id: availabilityChangeId,
          availabilityChangeId,
        })
      } else {
        await createMutation.mutateAsync(payload)
      }

      toast.success(isEditing ? 'Availability change updated' : 'Availability change created')
      closeSurface()
    } catch (error) {
      const fieldErrors = serverFieldErrors(error)?.fields

      if (isRecord(fieldErrors)) {
        setServerErrorState({
          key: serverErrorScopeKey,
          errors: fieldErrors,
        })
      }

      toast.error(
        isEditing ? 'Failed to update availability change' : 'Failed to create availability change',
        {
          description: getErrorMessage(error),
        }
      )
    }
  }

  const handleDelete = async () => {
    if (!changeId) {
      return
    }

    try {
      await deleteMutation.mutateAsync(changeId)
      toast.success('Availability change deleted')
      closeSurface()
    } catch (error) {
      toast.error('Failed to delete availability change', {
        description: getErrorMessage(error),
      })
    }
  }

  const StoreClosedIcon = quickCreateStoreClosedOption.icon

  const availabilityFields = (
    <div className={quickCreateFormSurfaceClassName}>
      <div className={quickCreateFieldGroupClassName}>
        <Typography variant="label" {...quickCreateFieldLabelProps}>
          Dates
        </Typography>
        <ReportDateRangeFilterBar
          calendarIntent="future"
          className={quickCreateDateRangePickerClassName}
          triggerClassName={quickCreateDateRangeTriggerClassName}
          initialFilters={{
            start_date: startDate,
            end_date: endDate,
          }}
          presetKeys={[]}
          showSingleDayAsRange
          showTriggerChevron
          onApply={({ start_date, end_date }) => {
            clearServerErrors()
            clearSubmitHelp()
            setStartDate(start_date)
            setEndDate(end_date)
          }}
        />
        <Typography variant="caption" className="block">
          {quickCreateDateRangeHelperText}
        </Typography>
        <QuickCreateServerErrors errors={serverErrors} fields={['start_date', 'end_date']} />
      </div>

      <div className="space-y-2">
        <Typography variant="label" {...quickCreateFieldLabelProps}>
          Store Status
        </Typography>
        <SelectableSurface
          type="button"
          selected={effects.store_closed}
          radius="surface"
          padding="none"
          className="flex w-full items-start gap-3 px-3 py-3"
          onClick={() => handleEffectToggle('store_closed')}
        >
          <StyledIcon
            icon={StoreClosedIcon}
            tone={
              effects.store_closed
                ? getAvailabilityChangePresentation('store_closed').tone
                : 'neutral'
            }
            className="mt-0.5"
            iconClassName="size-4"
          />
          <span className="min-w-0 text-left">
            <span className="block text-sm font-semibold text-foreground">
              {quickCreateStoreClosedOption.label}
            </span>
            <span className="mt-0.5 block text-xs leading-5">
              {quickCreateStoreClosedOption.description}
            </span>
          </span>
        </SelectableSurface>
        <QuickCreateServerErrors errors={serverErrors} fields={['store_closed']} />
      </div>

      <div className="space-y-2">
        <Typography variant="label" {...quickCreateFieldLabelProps}>
          Website Ordering
        </Typography>
        <div className="grid gap-2">
          {quickCreateWebsiteEffectOptions.map((option) => {
            const Icon = option.icon
            const isNoPickup = option.value === 'pickup_paused'
            const isNoDelivery = option.value === 'delivery_paused'
            const isDesignersChoice = option.value === 'designers_choice_only'
            const isDesignersChoiceDisabled =
              isDesignersChoice &&
              (!canUseDesignersChoiceOnly || !selectedLocationsCanToggleDesignersChoice) &&
              !effects.designers_choice_only
            const isDisabled = isDesignersChoiceDisabled
            const isSelected =
              (isNoPickup && effects.pickup_paused) ||
              (isNoDelivery && effects.delivery_paused) ||
              (isDesignersChoice && effects.designers_choice_only)
            const description =
              isDesignersChoice && !canUseDesignersChoiceOnly
                ? DESIGNERS_CHOICE_ONLY_EMPTY_COPY
                : isDesignersChoice && !selectedLocationsCanToggleDesignersChoice
                  ? DESIGNERS_CHOICE_ONLY_NO_FULFILLMENT_COPY
                  : option.description

            return (
              <SelectableSurface
                key={option.value}
                type="button"
                selected={isSelected}
                radius="surface"
                padding="none"
                className="flex items-start gap-3 px-3 py-3 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={isDisabled}
                onClick={() => handleEffectToggle(option.value)}
              >
                <StyledIcon
                  icon={Icon}
                  tone={
                    isSelected ? getAvailabilityChangePresentation(option.value).tone : 'neutral'
                  }
                  className="mt-0.5"
                  iconClassName="size-4"
                />
                <span className="min-w-0 text-left">
                  <span className="block text-sm font-semibold text-foreground">
                    {option.label}
                  </span>
                  <span className="mt-0.5 block text-xs leading-5">{description}</span>
                </span>
              </SelectableSurface>
            )
          })}
        </div>
        <QuickCreateServerErrors
          errors={serverErrors}
          fields={[
            'pickup_paused',
            'delivery_paused',
            'designers_choice_only',
            'temporary_hours',
            'effects',
          ]}
        />
      </div>

      {shouldShowLocationPicker ? (
        <div className="space-y-2">
          <Typography variant="label" {...quickCreateFieldLabelProps}>
            Locations
          </Typography>
          <div className="flex flex-wrap gap-2">
            {activeLocations.map((location) => {
              const locationId = String(location.id)
              const isSelected = selectedLocationIds.includes(locationId)

              return (
                <ToggleButton
                  key={locationId}
                  type="button"
                  pressed={isSelected}
                  size="xs"
                  className="min-w-0 gap-2 px-2.5 has-[>svg]:px-2.5"
                  onClick={() => toggleLocation(locationId)}
                >
                  {isSelected ? (
                    <Check className="size-3.5 shrink-0" />
                  ) : (
                    <MapPin className="size-3.5 shrink-0" />
                  )}
                  <span className="max-w-44 truncate">{getLocationName(location)}</span>
                </ToggleButton>
              )
            })}
          </div>
          {submitHelpMessage && selectedLocationCount === 0 ? (
            <Typography variant="label" tone="destructive" className="block">
              {submitHelpMessage}
            </Typography>
          ) : null}
          <QuickCreateServerErrors errors={serverErrors} fields={['location_ids']} />
        </div>
      ) : activeLocations.length === 0 ? (
        <Alert variant="destructive">
          <AlertDescription>{noActiveLocationsMessage}</AlertDescription>
        </Alert>
      ) : null}

      <RecurrenceChoice isRecurringYearly={isRecurringYearly} onChange={setIsRecurringYearly} />
    </div>
  )
  const sheetTitle = isEditing ? 'Availability Change' : 'Schedule Availability'
  const sheetSubtitle = isEditing
    ? 'Update the store closure or website ordering limits for these dates.'
    : 'Create a store closure or website ordering limits for these dates.'

  return (
    <QuickCreateSurface
      ariaLabel="Schedule closure or limit"
      isSheet={isSheet}
      onClose={closeSurface}
      onSheetCloseAnimationEnd={onClosed}
      onSheetOpenChange={onOpenChange}
      onSubmit={handleSubmit}
      panelRef={panelRef}
      sheetOpen={open}
      type="form"
    >
      <OrderDetailPanel
        title={sheetTitle}
        subtitle={sheetSubtitle}
        icon={Ban}
        tone="periwinkle"
        className="shadow-none"
      >
        {availabilityFields}
      </OrderDetailPanel>

      <div
        data-slot="availability-sheet-footer"
        className={cn(
          'flex shrink-0 flex-wrap items-center justify-between gap-2',
          quickCreateFooterClassName
        )}
      >
        {isEditing && canDelete ? (
          <ConfirmationDialog
            variant="destructive"
            title="Delete availability change"
            description="This removes the closure or availability limit from the selected dates."
            confirmText="Delete"
            pendingText="Deleting..."
            open={isDeleteDialogOpen}
            onOpenChange={setIsDeleteDialogOpen}
            isPending={deleteMutation.isPending}
            onConfirm={handleDelete}
            trigger={
              <Button type="button" variant="ghostDestructive" className="mr-auto">
                <Trash className="size-4" />
                Delete
              </Button>
            }
          />
        ) : null}
        <div className={cn('ml-auto', quickCreateFooterButtonGroupClassName)}>
          <Button type="button" variant="outline" onClick={closeSurface}>
            Cancel
          </Button>
          <div
            className="inline-flex"
            onClick={() => {
              if (canSave) {
                return
              }

              setSubmitHelpMessage(
                selectedLocationCount === 0
                  ? locationRequiredMessage
                  : 'Choose at least one availability option before saving.'
              )
            }}
          >
            <Button type="submit" disabled={!canSave || mutation.isPending}>
              {isEditing ? 'Save' : 'Create'}
            </Button>
          </div>
        </div>
      </div>
    </QuickCreateSurface>
  )
}
